<template>
  <!-- Client-only: gated behind `mounted` so this <Teleport> is absent from
       BOTH the SSR snapshot (renderToString never runs onMounted) and the
       client's first hydration render (onMounted hasn't fired yet). That
       makes the two sides agree exactly. Previously the Teleport existed on
       both sides but its target (body) held no SSR-injected teleport buffer,
       so hydration tried to reconcile the toast wrapper against body's real
       children and warned ("Hydration class mismatch ... class=null").
       After mount the Teleport appears client-side as usual, so toasts still
       animate (the TransitionGroup simply mounts empty one tick after
       hydration). See core/prerender.py / static/src/prerender-ssr.js. -->
  <Teleport v-if="mounted" to="body">
    <!-- no-print: teleported to <body>, so it lands after AppFooter in DOM
         order — if a toast fires during the headless PDF page load, print
         CSS's `.fixed { position: static }` would otherwise pull it out of
         its floating corner and into the document flow right after the
         footer. A live-page notification has no business in a static PDF
         regardless of why it fired. See CLAUDE.md §9 #54. -->
    <div class="no-print fixed top-4 right-4 z-[1100] flex flex-col gap-2 w-full max-w-sm px-4 sm:px-0 pointer-events-none">
      <TransitionGroup name="toast" tag="div" class="flex flex-col gap-2">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="pointer-events-auto card shadow-float border-l-4 flex items-start gap-3 p-3.5 pr-4"
          :class="borderClass(t.type)"
        >
          <span :class="iconWrapClass(t.type)" v-html="iconSvg(t.type)"></span>
          <div class="flex-1 min-w-0">
            <div v-if="t.title" class="text-sm font-semibold text-ink-900">{{ t.title }}</div>
            <div class="text-sm text-ink-700">{{ t.message }}</div>
          </div>
          <button class="text-ink-500 hover:text-ink-700 transition-colors shrink-0" @click="dismiss(t.id)" aria-label="Dismiss">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const toasts = ref([]);
let counter = 0;

// See template comment: keeps <Teleport> out of SSR + the first hydration
// render so the prerendered snapshot and the client agree node-for-node.
const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});

const push = (message, { type = 'info', title = '', duration = 4000 } = {}) => {
  const id = ++counter;
  toasts.value.push({ id, message, type, title });
  if (duration > 0) setTimeout(() => dismiss(id), duration);
};

const dismiss = (id) => {
  toasts.value = toasts.value.filter(t => t.id !== id);
};

defineExpose({ push, dismiss });

// Globally accessible helper (so any page can call window.$toast(...))
if (typeof window !== 'undefined') {
  window.$toast = (message, opts) => push(message, opts);
}

const borderClass = (type) => ({
  success: 'border-accent-500',
  error:   'border-danger-500',
  warn:    'border-warn-500',
  info:    'border-brand-500',
}[type] || 'border-brand-500');

const iconWrapClass = (type) => {
  const base = 'w-8 h-8 shrink-0 rounded-full flex items-center justify-center';
  return `${base} ${{
    success: 'bg-accent-50 text-accent-600',
    error:   'bg-danger-50 text-danger-600',
    warn:    'bg-warn-50 text-warn-700',
    info:    'bg-brand-50 text-brand-600',
  }[type] || 'bg-brand-50 text-brand-600'}`;
};

const iconSvg = (type) => {
  const paths = {
    success: '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>',
    error:   '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>',
    warn:    '<path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>',
    info:    '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd"/>',
  };
  return `<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">${paths[type] || paths.info}</svg>`;
};
</script>

<style scoped>
.toast-enter-active, .toast-leave-active { transition: all 220ms cubic-bezier(.22,.61,.36,1); }
.toast-enter-from { opacity: 0; transform: translateX(20px) scale(.98); }
.toast-leave-to   { opacity: 0; transform: translateX(20px) scale(.98); }
</style>
